import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

export interface BaseDropdownOption {
  id: string;
  label: string;
  emoji: string;
  description: string;
}

interface BaseDropdownProps<T extends BaseDropdownOption> {
  value: string;
  onChange: (value: string) => void;
  options: T[];
  className?: string;
  placeholder?: string;
  minWidth?: string;
}

export function BaseDropdown<T extends BaseDropdownOption>({
  value,
  onChange,
  options,
  className = '',
  placeholder = 'Select...',
  minWidth = 'min-w-[180px]'
}: BaseDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [menuRect, setMenuRect] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });

  // Compute and set menu position relative to viewport
  const updateMenuPosition = () => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 8;
    const width = Math.max(rect.width, 288); // ensure usable width
    let left = rect.left;
    const rightOverflow = left + width - window.innerWidth;
    if (rightOverflow > 0) left = Math.max(8, left - rightOverflow - 8);
    const top = rect.bottom + gap;
    setMenuRect({ top, left, width });
  };

  useLayoutEffect(() => {
    if (isOpen) updateMenuPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onResize = () => updateMenuPosition();
    const onScroll = () => updateMenuPosition();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  // Lock body scroll with scrollbar compensation
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [isOpen]);

  const selectedOption = options.find(option => option.id === value) || options[0];

  const handleSelect = (optionId: string) => {
    onChange(optionId);
    setIsOpen(false);
  };

  return (
    <div className={`relative ${minWidth} ${className}`}>
      <button
        ref={buttonRef}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((o) => !o)}
        className="flex items-center justify-between w-full px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
      >
        <span className="flex items-center gap-2">
          <span>{selectedOption?.emoji}</span>
          <span className="text-gray-700">{selectedOption?.label || placeholder}</span>
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {isOpen && createPortal(
        <>
          <div
            className="fixed inset-0 z-[60] overflow-hidden overscroll-contain"
            onClick={() => setIsOpen(false)}
          />
          <div
            className="fixed z-[70] bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-auto max-h-96"
            style={{ top: menuRect.top, left: menuRect.left, width: `${menuRect.width}px` }}
            role="listbox"
          >
            <div className="py-1">
              {options.map((option) => (
                <button
                  key={option.id}
                  onClick={() => handleSelect(option.id)}
                  className={`group w-full flex flex-col items-start gap-0.5 px-4 py-2.5 text-sm text-left transition-colors ${
                    option.id === value ? 'bg-primary-50' : 'hover:bg-primary-25'
                  }`}
                  role="option"
                  aria-selected={option.id === value}
                >
                  <div className="flex items-center gap-2">
                    <span>{option.emoji}</span>
                    <span
                      className={`$${''}
                        ${option.id === value
                          ? 'text-primary-600 font-semibold'
                          : 'text-gray-800 font-medium group-hover:text-primary-600'}
                      `}
                    >
                      {option.label}
                    </span>
                  </div>
                  <p
                    className={`text-xs pl-6 ${
                      option.id === value ? 'text-primary-500' : 'text-gray-500 group-hover:text-primary-400'
                    }`}
                  >
                    {option.description}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
} 